import {
  ActionSchema,
  DynamicBooleanSchema,
  DynamicStringSchema,
  DynamicValueSchema,
} from '@a2ui/web_core/v0_9';
import { createComponentImplementation } from '@a2ui/react/v0_9';
import { defineA2uiComponentSchema } from '../../a2ui-schema';
import {
  ViewChangeProposal,
  type ProposalPreview,
  type ProposalResult,
  type ProposedChange,
} from './ViewChangeProposal';

interface ResolvedProps {
  readonly title?: string;
  readonly rationale?: string;
  readonly changes?: ProposedChange[];
  // Both are written reactively by the host at `/preview/<id>` and
  // `/results/<id>`. Neither is ever sent by the agent: the preview is the
  // console's own pricing, and the result reports what applying actually did.
  readonly preview?: ProposalPreview;
  readonly result?: ProposalResult;
  readonly pending?: boolean;
  readonly onApply?: () => void;
  readonly onDismiss?: () => void;
  readonly onPreview?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = defineA2uiComponentSchema({
  title: DynamicStringSchema,
  rationale: DynamicStringSchema,
  // A literal array of {label, from, to} rows, fixed when the proposal is
  // made — no dynamic binding needed, same as EditQuote's changes.
  changes: DynamicValueSchema,
  preview: DynamicValueSchema,
  result: DynamicValueSchema,
  pending: DynamicBooleanSchema,
  onApply: ActionSchema,
  onDismiss: ActionSchema,
  // Bound by the agent with the SAME action context as `onApply`, and fired by
  // the component on mount rather than by a click. Declared here or the binder
  // drops it and the card renders unpriced.
  onPreview: ActionSchema,
}) as any;

export const ViewChangeProposalImpl = createComponentImplementation(
  { name: 'ViewChangeProposal', schema },
  ({
    props,
  }: {
    props: ResolvedProps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildChild: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: any;
  }) => (
    <ViewChangeProposal
      title={props.title ?? 'Suggested changes'}
      rationale={props.rationale}
      changes={
        Array.isArray(props.changes)
          ? (props.changes as ProposedChange[])
          : undefined
      }
      preview={props.preview as ProposalPreview | undefined}
      result={props.result as ProposalResult | undefined}
      pending={props.pending === true}
      onApply={props.onApply}
      onDismiss={props.onDismiss}
      onPreview={props.onPreview}
    />
  )
);
